import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ledgerApi, aiApi } from "../../lib/ledgerApi";
import HashString from "../../components/HashString";
import RiskPanel from "../../components/RiskPanel";
import FederationBadge from "../../components/FederationBadge";
import ProofPanel from "../../components/ProofPanel";
import WebhookDeliveriesCard from "../../components/WebhookDeliveriesCard";

const KV = ({ label, children, mono }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-border last:border-0">
    <div className="col-span-12 sm:col-span-3 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-muted-foreground self-center">{label}</div>
    <div className={`col-span-12 sm:col-span-9 ${mono ? "font-mono" : ""} text-sm break-all`}>{children}</div>
  </div>
);

export default function AppTxDetail() {
  const { id } = useParams();
  const [tx, setTx] = useState(null);
  const [risk, setRisk] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setTx(null);
    setRisk(null);
    setError(null);
    ledgerApi.getTx(id)
      .then((t) => alive && setTx(t))
      .catch((e) => alive && setError(e?.response?.data?.detail || e.message));
    aiApi.txRisk(id)
      .then((r) => alive && setRisk(r))
      .catch(() => alive && setRisk({ available: false }));
    const i = setInterval(() => {
      ledgerApi.getTx(id).then((t) => alive && setTx(t)).catch(() => {});
    }, 4000);
    return () => { alive = false; clearInterval(i); };
  }, [id]);

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14" data-testid="app-tx-detail">
      <Link to="/app/transactions" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-transactions">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to transactions
      </Link>
      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">/ Transaction</div>
      <h1 className="text-2xl sm:text-3xl tracking-tight font-medium mb-8 break-all font-mono" data-testid="app-tx-detail-id">{id}</h1>

      {error && <div className="border border-status-tampered/40 bg-status-tampered/10 p-4 rounded-sm text-status-tampered text-sm">{String(error)}</div>}
      {!tx && !error && <div className="text-sm text-muted-foreground">Loading…</div>}

      {tx && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-border rounded-sm bg-card p-4 sm:p-6">
            <KV label="Status" mono>
              <span
                className={`px-2 py-0.5 border rounded-sm text-xs uppercase tracking-widest ${
                  tx.status === "confirmed"
                    ? "border-status-ok/40 bg-status-ok/10 text-status-ok"
                    : "border-status-info/40 bg-status-info/10 text-status-info"
                }`}
                data-testid="app-tx-status"
              >
                {tx.status}
              </span>
              {tx.block_number != null && (
                <Link to={`/blocks/${tx.block_number}`} className="ml-3 text-xs text-muted-foreground hover:underline">
                  in block #{tx.block_number}
                </Link>
              )}
              {tx.block_number != null && (
                <span className="ml-3 inline-block">
                  <FederationBadge blockNumber={tx.block_number} testid="app-tx-federation-badge" size="sm" />
                </span>
              )}
            </KV>
            <KV label="Kind" mono>
              {tx.kind}
              {tx.bundle_role && (
                <span className="ml-2 px-1.5 py-0.5 border border-status-info/40 bg-status-info/10 text-status-info text-[10px] uppercase tracking-widest rounded-sm">
                  {tx.bundle_role}
                </span>
              )}
            </KV>
            {tx.bundle_id && (
              <KV label="Bundle" mono>
                <HashString value={tx.bundle_id} label="Bundle id" />
              </KV>
            )}
            <KV label="From" mono>
              <Link to={`/wallets/${tx.from_address}`} className="hover:underline">{tx.from_address}</Link>
            </KV>
            <KV label="To" mono>
              <Link to={`/wallets/${tx.to_address}`} className="hover:underline">{tx.to_address}</Link>
            </KV>
            <KV label="Amount" mono>{tx.amount} <span className="text-muted-foreground">{tx.currency}</span></KV>
            {tx.memo && <KV label="Memo">{tx.memo}</KV>}
            <KV label="Timestamp" mono>{new Date(tx.timestamp).toLocaleString()}</KV>
            <KV label="Pubkey" mono><HashString value={tx.pubkey} label="Pubkey" /></KV>
            <KV label="Signature" mono><HashString value={tx.signature} label="Signature" /></KV>
            <KV label="Public explorer" mono>
              <Link to={`/tx/${tx.tx_id}`} className="text-xs underline text-muted-foreground hover:text-foreground" data-testid="public-explorer-link">
                view on public block explorer (no PII)
              </Link>
            </KV>
            {tx.status === "confirmed" && tx.block_number != null && (
              <div className="mt-6" data-testid="app-tx-proof-panel-wrap">
                <ProofPanel txId={tx.tx_id} />
              </div>
            )}
            <WebhookDeliveriesCard txId={tx.tx_id} />
          </div>
          <div className="lg:col-span-1">
            <RiskPanel data={risk} />
          </div>
        </div>
      )}
    </div>
  );
}
